"use client"

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ProjectService from '@/shared/services/ProjectService';
import type { Project } from '@/shared/types';
import { Loading, Alert, ProjectPopup } from '@/shared/components/ui';
import { logger } from '@/shared/utils';
import { useSafeThemeClasses } from '@/shared/theme';
import { SEO } from '@/shared/components/SEO';

export default function ProjectPage() {
  const theme = useSafeThemeClasses();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [popupProject, setPopupProject] = useState<Project | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  
  // Get project ID from URL if available
  const { id } = useParams<{ id?: string }>();
  
  // Filter unique tags from projects
  const uniqueTags = [...new Set(projects.flatMap(project => project.tags || []))];

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        setError(null);
        
        if (id) {
          // Fetch a single project by ID
          const project = await ProjectService.getProjectById(id);
          if (project) {
            setSelectedProject(project);
            setProjects([project]);
            logger.info(`Project loaded: ${project.title}`, 'APP');
          } else {
            setError('Project not found');
          }
        } else {
          // Fetch all projects
          const projects = await ProjectService.getAllProjects();
          setProjects(projects);
          logger.info(`${projects.length} projects loaded`, 'APP');
        }
      } catch (err) {
        logger.error('Failed to load projects', 'ProjectPage', { error: err });
        setError('Failed to load projects. Please try again later.');
      } finally {
        setIsLoading(false);
        
        // Trigger fade-in animation after a brief delay to ensure theme is applied
        setTimeout(() => {
          setIsVisible(true);
        }, 50);
      }
    }
    
    fetchData();
  }, [id]);

  // Handle tag filter change
  const handleFilterChange = (tag: string) => {
    setActiveFilter(tag);
  };

  // Handle opening project popup with proper scroll lock
  const handleProjectClick = (project: Project) => {
    setPopupProject(project);
    setIsPopupOpen(true);
  };

  // Handle closing project popup
  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setTimeout(() => {
      setPopupProject(null);
    }, 200); // Wait for animation to complete
  };
  
  // Filter projects based on selected tag
  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.tags?.includes(activeFilter));
  
  // Render a single project view
  if (id && selectedProject) {
    return (
      <div className={`min-h-screen pt-24 pb-16 ${theme.background}`}>
        <SEO
          title={selectedProject.title}
          description={selectedProject.description}
          path={`/projects/${selectedProject.id}`}
          image={selectedProject.image}
        />
        <div className={`transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          {/* Background decoration */}
          <div className='absolute inset-0 overflow-hidden pointer-events-none'>
            <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]}`}></div>
            <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]}`}></div>
          </div>

        <div className="container mx-auto px-4 sm:px-6 relative">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loading size="lg" text="Loading project details..." />
            </div>
          ) : error ? (
            <Alert type="error" message={error} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="mb-8">
                  <div className={`rounded-2xl overflow-hidden shadow-lg elegant-hover float-animation ${theme.card}`}>
                    {selectedProject.image ? (
                      <img 
                        src={selectedProject.image} 
                        alt={selectedProject.title} 
                        className="w-full h-auto object-cover aspect-video"
                        loading="eager"
                        style={{ objectPosition: 'center' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="600"><rect fill="%23e5e7eb" width="1200" height="600"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="24" fill="%236b7280">${selectedProject.title}</text></svg>`)}`;
                        }}
                      />
                    ) : (
                      <div className={`w-full aspect-video flex items-center justify-center ${theme.card}`}>
                        <div className="text-center">
                          <svg className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className={theme.textMuted}>No image available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={`prose prose-lg max-w-none ${theme.card} glass-card-elegant p-8 rounded-2xl`}>
                  <h1 className={`text-4xl font-bold mb-6 ${theme.textHeading}`}>{selectedProject.title}</h1>
                  <p className={`text-lg mb-8 ${theme.textSecondary}`}>{selectedProject.description}</p>
                  
                  {selectedProject.details?.overview && selectedProject.details.overview.length > 0 && (
                    <>
                      <h2 className={`text-2xl font-semibold mt-12 mb-4 ${theme.textHeading}`}>Project Overview</h2>
                      {selectedProject.details.overview.map((paragraph, index) => (
                        <p key={`overview-${index}`} className={`${theme.textPrimary} ${index > 0 ? 'mt-4' : ''}`}>
                          {paragraph}
                        </p>
                      ))}
                    </>
                  )}
                      
                  {selectedProject.details?.solution && selectedProject.details.solution.length > 0 && (
                    <>
                      <h2 className={`text-2xl font-semibold mt-12 mb-4 ${theme.textHeading}`}>Solution</h2>
                      {selectedProject.details.solution.map((paragraph, index) => (
                        <p key={`solution-${index}`} className={`${theme.textPrimary} ${index > 0 ? 'mt-4' : ''}`}>
                          {paragraph}
                        </p>
                      ))}
                    </>
                  )}
                      
                  {selectedProject.details?.results && selectedProject.details.results.length > 0 && (
                    <>
                      <h2 className={`text-2xl font-semibold mt-12 mb-4 ${theme.textHeading}`}>Results</h2>
                      <ul className={`${theme.textPrimary} list-disc list-inside space-y-2`}>
                        {selectedProject.details.results.map((result, index) => (
                          <li key={`result-${index}`}>{result}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {(!selectedProject.details || 
                    (!selectedProject.details.overview?.length && 
                     !selectedProject.details.solution?.length && 
                     !selectedProject.details.results?.length)) && (
                    <div className={`mt-8 p-4 rounded-lg ${theme.alertWarning}`}>
                      <p className={theme.textSecondary}>
                        Additional project details are being prepared. Check back soon for more information.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className={`p-6 rounded-xl mb-6 ${theme.card}`}>
                    <h3 className={`text-xl font-semibold mb-4 ${theme.textHeading}`}>Project Details</h3>
                    
                    <div className="space-y-3">
                      {selectedProject.client && (
                        <div>
                          <div className={`text-sm font-medium ${theme.textMuted}`}>Client</div>
                          <div className={theme.textPrimary}>{selectedProject.client}</div>
                        </div>
                      )}
                      
                      {selectedProject.completionDate && (
                        <div>
                          <div className={`text-sm font-medium ${theme.textMuted}`}>Completed</div>
                          <div className={theme.textPrimary}>{selectedProject.completionDate}</div>
                        </div>
                      )}
                      
                      <div>
                        <div className={`text-sm font-medium ${theme.textMuted}`}>Technologies</div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedProject.tags?.map((tag: string, index: number) => (
                            <span key={`${tag}-${index}`} className={`px-2 py-1 text-xs rounded-full ${theme.badge}`}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {selectedProject.liveUrl && (
                    <div className="text-center">
                      <a 
                        href={selectedProject.liveUrl || selectedProject.link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`inline-block w-full py-3 px-6 text-center rounded-xl transition-all duration-300 text-base font-semibold ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 hover:shadow-xl glass-highlight`}
                      >
                        Visit Project
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  }
  
  // Render projects list view
  return (
    <div className={`min-h-screen pt-24 pb-16 ${theme.background}`}>
      <SEO title="Projects" description="A portfolio of projects and case studies." path="/projects" />
      <div className={`transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        {/* Background decoration */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]}`}></div>
          <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]}`}></div>
        </div>

      <div className="container mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-16">
          <h1 className={`text-4xl font-bold mb-6 ${theme.textHeading}`}>Our Projects</h1>
          <p className={`text-xl max-w-3xl mx-auto ${theme.textSecondary}`}>
            Here are some of the interesting projects and products I have worked on.
          </p>
        </div>
        
        {/* Tag filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <div
            onClick={() => handleFilterChange('all')}
            className={`py-2 px-4 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer ${theme.buttonSecondary} ${theme.buttonTextSecondary} hover:scale-105 ${
              activeFilter === 'all' ? 'glass-highlight glass-active' : 'glass-active'
            }`}
            role="button"
            tabIndex={0}
          >
            All Projects
          </div>
          
          {uniqueTags.map(tag => (
            <div
              key={tag}
              onClick={() => handleFilterChange(tag)}
              className={`py-2 px-4 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer ${theme.buttonSecondary} ${theme.buttonTextSecondary} hover:scale-105 ${
                activeFilter === tag ? 'glass-highlight glass-active' : 'glass-active'
              }`}
              role="button"
              tabIndex={0}
            >
              {tag}
            </div>
          ))}
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loading size="lg" text="Loading projects..." />
          </div>
        ) : error ? (
          <Alert type="error" message={error} />
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-16">
            <p className={`text-xl ${theme.textSecondary}`}>No projects found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div 
                key={project.id} 
                className="block h-full group cursor-pointer"
                onClick={() => handleProjectClick(project)}
              >
                <div className={`rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col ${theme.card}`}>
                  <div className={`aspect-video overflow-hidden ${theme.card}`}>
                    {project.image ? (
                      <img 
                        src={project.image} 
                        alt={project.title} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        style={{ objectPosition: 'center' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center ${project.image ? 'hidden' : ''} ${theme.card}`}>
                      <div className="text-center p-4">
                        <svg className={`w-12 h-12 mx-auto mb-2 ${theme.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className={`text-xs ${theme.textMuted}`}>No image</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col">
                    <h3 className={`text-xl font-semibold mb-3 group-hover:${theme.textAccent} transition-colors duration-300 ${theme.textHeading}`}>
                      {project.title}
                    </h3>
                    {project.client && (
                      <p className={`text-sm mb-2 ${theme.textMuted}`}>{project.client}</p>
                    )}
                    <p className={`mb-4 flex-grow ${theme.textSecondary}`}>
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags?.map((tag: string, index: number) => (
                        <span key={`project-${project.id}-tag-${tag}-${index}`} className={`px-2 py-1 text-xs rounded-full ${theme.badge}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Project Popup */}
        <ProjectPopup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          project={popupProject}
        />
      </div>
      </div>
    </div>
  );
}